import React, { useState } from "react";
import { Button, message, Modal } from "antd";
import type GlobalStore from "@/store";
import ShopType from "@/enum/ShopType";
import UserType from "@/enum/UserType";
import { useGlobalStore } from "@/store/hooks";
import { useModalStore } from "@/hooks/Modal/hooks";
import PremiumUpgradeModal from "@/components/PremiumUpgradeModal";
import AppVersion from "@/enum/AppVersion";
import { InjectBaseModalProps, ModalStore } from "@/hooks/Modal/ModalStore";
import { InfoCircleOutlined } from "@ant-design/icons";
import AutoLoadingButton from "@/components/AutoLoadingButton";
import { getOrderVolumeLimit, getOrderVolumeTipName } from "@/scenes/setting/Shop/OrderVolume/services";
import { UserStatus } from "@/dto/user/UserDTO";
import { PremiumFunctionType, FromFeature, PremiumFunctionTypeNameMap } from "./PremiumValidator.type";
import ShopTypeNameMap from "@/map/ShopTypeNameMap";

export * from "./PremiumValidator.type";

interface PremiumValidatorParams {
    type: PremiumFunctionType;

    shopId: number;

    globalStore: typeof GlobalStore;

    modalStore: ModalStore;

    fromFeature?: FromFeature;
}

// hooks 高级版验证器
export function usePremiumValidator() {
    const globalStore = useGlobalStore();
    const modalStore = useModalStore();
    const [loading, setLoading] = useState(false);

    const validate = async (params: Omit<PremiumValidatorParams, "globalStore" | "modalStore">) => {
        try {
            setLoading(true);

            return await PremiumValidator.validate({
                ...params,
                globalStore,
                modalStore,
            });
        } finally {
            setLoading(false);
        }
    };

    return {
        validate,
        loading,
    };
}

const _GLORY_CACHE_KEY = "_GLORY_CACHE_KEY__";

function getGloryTypeName(type: PremiumFunctionType) {
    switch (type) {
        case PremiumFunctionType.ORDER_SPLITTING:
            return "拆分订单";
        case PremiumFunctionType.OPERATION_LOG:
            return "操作日志";
        case PremiumFunctionType.LOGISTICS_AUDIT:
            return "快递对账";

        default:
            return "";
    }
}

function showGloryTip(shopName: string, type: PremiumFunctionType) {
    const cache = localStorage.getItem(type + _GLORY_CACHE_KEY);

    if (!cache) {
        return new Promise<void>((yes) => {
            function callback() {
                localStorage.setItem(type + _GLORY_CACHE_KEY, "1");
                yes();
            }

            const typeName = getGloryTypeName(type);

            Modal.info({
                title: "温馨提示",
                content: `【${shopName}】店铺是高级版上线前已使用发货易的店铺，免费赠送使用“${typeName}”功能。新店铺需要订购高级版才能使用`,
                okText: "知道了",
                onOk: callback,
                onCancel: callback,
            });
        });
    }

    return Promise.resolve();
}

interface NeedUpgradeModalProps extends InjectBaseModalProps {
    modalStore: ModalStore;

    title: React.ReactNode;

    content?: React.ReactNode;

    type: PremiumFunctionType;

    shopId: number;

    fromFeature?: FromFeature;

    okText?: string;
}

export const NeedUpgradeModal: React.FC<NeedUpgradeModalProps> = React.memo(function (props) {
    const { open, onCancel, title, content, modalStore, shopId, type, fromFeature, okText = "升级版本" } = props;

    const globalStore = useGlobalStore();

    return (
        <Modal width={416} title={null} open={open} onCancel={onCancel} footer={null}>
            <div className="ant-modal-confirm-body-wrapper mt20">
                <div className="ant-modal-confirm-body">
                    <InfoCircleOutlined className="text-primary" />

                    <span className="ant-modal-confirm-title">{title}</span>

                    {content ? <div className="ant-modal-confirm-content">{content}</div> : null}
                </div>

                <div style={{ display: "flex", justifyContent: "right" }} className="mt20">
                    <AutoLoadingButton
                        className="mr10"
                        onClick={async () => {
                            await globalStore.syncAllOwnerShopSubscription(shopId);

                            await globalStore.getShopSetting([shopId]);

                            message.success("更新版本成功");

                            onCancel();
                        }}
                    >
                        已订高级版，更新版本
                    </AutoLoadingButton>

                    <Button
                        type="primary"
                        onClick={() => {
                            modalStore.openModal(PremiumUpgradeModal, {
                                shopId,
                                type,
                                fromFeature,
                            });

                            onCancel();
                        }}
                    >
                        {okText}
                    </Button>
                </div>
            </div>
        </Modal>
    );
});

// 高级版功能校验
export default class PremiumValidator {
    /**
     * 部分高级版功能从可不可用来区分版本转换成按量来区分版本
     *
     */
    static async showShopPremiumModal(params: PremiumValidatorParams) {
        let { fromFeature } = params;
        const { type, shopId, globalStore, modalStore } = params;
        const shop = globalStore.shopMapping.get(shopId);
        const shopName = shop?.name;
        const ownerShop = globalStore.shopMapping.get(shop.ownerShopId);
        const ownerShopName = ownerShop?.name;

        fromFeature = type || fromFeature;

        switch (type) {
            case PremiumFunctionType.FREQUENTLY_USED_ORDER_SEARCH_CRITERIA:
                const content = ownerShop ? (
                    <span className="text-error">
                        【{shopName}】店铺所属店铺为【{ownerShopName}】店铺，升级【{ownerShopName}】店铺即可
                    </span>
                ) : null;

                modalStore.openModal(NeedUpgradeModal, {
                    title: `【${shopName}】店铺是标准版本，最多只能添加和使用1个常用搜索条件，需要使用更多则欢迎【${shopName}】店铺升级后使用`,
                    content,
                    shopId,
                    type,
                    fromFeature,
                    modalStore,
                });

                break;

            case PremiumFunctionType.ORDER_SPLITTING: {
                const content = ownerShop ? (
                    <span className="text-error">
                        【{shopName}】店铺所属店铺为【{ownerShopName}】店铺，升级【{ownerShopName}】店铺即可
                    </span>
                ) : null;

                modalStore.openModal(NeedUpgradeModal, {
                    title: `【${shopName}】店铺是标准版本，最多只能进行200次拆分订单，需要继续拆单则欢迎【${shopName}】店铺升级后使用`,
                    content,
                    shopId,
                    type,
                    fromFeature,
                    modalStore,
                });

                break;
            }

            case PremiumFunctionType.LOGISTICS_AUDIT: {
                const content = ownerShop ? (
                    <span className="text-error">
                        【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                    </span>
                ) : null;

                modalStore.openModal(NeedUpgradeModal, {
                    title: `【${shop.name}】店铺是标准版本，最多只能进行10次快递对账，需要继续对账则需要升级，欢迎【${shop.name}】店铺升级后使用`,
                    content,
                    shopId,
                    type,
                    fromFeature,
                    modalStore,
                });
                break;
            }

            case PremiumFunctionType.OPERATION_LOG: {
                const content = ownerShop ? (
                    <span className="text-error">
                        【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                    </span>
                ) : null;

                modalStore.openModal(NeedUpgradeModal, {
                    title: `【${shop.name}】店铺是标准版本，最多只能进行20次操作日志查询，需要继续查询则需要升级，欢迎【${shop.name}】店铺升级后使用`,
                    content,
                    shopId,
                    type,
                    fromFeature,
                    modalStore,
                });
                break;
            }

            case PremiumFunctionType.PRE_SHIPPING: {
                const content = ownerShop ? (
                    <span className="text-error">
                        【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                    </span>
                ) : null;

                modalStore.openModal(NeedUpgradeModal, {
                    title: `【${shop.name}】店铺是标准版本，最多只能进行200次预发货，需要继续使用预发货则欢迎【${shop.name}】店铺升级后使用`,
                    content,
                    shopId,
                    type,
                    fromFeature,
                    modalStore,
                });
                break;
            }
        }
    }

    static async getRealShopVersion(shopId: number, globalStore: typeof GlobalStore) {
        const shop = globalStore.shopMapping.get(shopId);
        const ownerShop = globalStore.shopMapping.get(shop.ownerShopId);

        const wantToLoadShopId = ownerShop?.id || shop.id;

        const shopVersion = await globalStore.getShopAppVersion(wantToLoadShopId);

        const isPremium = shopVersion.appVersion === AppVersion.PREMIUM;
        const isGloryV1 = shopVersion.appVersion === AppVersion.GLORY_V1;
        const isGlory = shopVersion.appVersion === AppVersion.GLORY;

        return {
            isPremium,
            isGlory,
            isGloryV1,
        };
    }

    /**
     * 已做缓存
     */
    static async validateShopVersion(shopId: number, globalStore: typeof GlobalStore) {
        const shop = globalStore.shopMapping.get(shopId);
        const ownerShop = globalStore.shopMapping.get(shop.ownerShopId);

        const wantToLoadShopId = ownerShop?.id || shop.id;
        let shopVersion = globalStore.shopAppVersionMapping.get(wantToLoadShopId);

        let shouldReloadShopSetting = false;
        // 如果不是高级版
        if (shopVersion?.appVersion !== AppVersion.PREMIUM) {
            shouldReloadShopSetting = true;
        }

        shopVersion = await globalStore.getShopAppVersion(wantToLoadShopId, shouldReloadShopSetting);

        const isPremium = shopVersion.appVersion === AppVersion.PREMIUM;
        const isGloryV1 = shopVersion.appVersion === AppVersion.GLORY_V1;
        const isGlory = shopVersion.appVersion === AppVersion.GLORY;

        return {
            isPremium,
            isGlory,
            isGloryV1,
            appVersion: shopVersion.appVersion,
        };
    }

    /**
     * 已做缓存
     * 通过promise resolve才能进行下一步流程
     */
    static async validate(params: PremiumValidatorParams): Promise<boolean> {
        const { type, shopId, globalStore, modalStore } = params;

        let { fromFeature } = params;

        const shop = globalStore.shopMapping.get(shopId);
        const shopName = shop?.name;
        const ownerShop = globalStore.shopMapping.get(shop.ownerShopId);
        const ownerShopName = ownerShop?.name;

        const { isPremium, isGlory, isGloryV1, appVersion } = await this.validateShopVersion(shopId, globalStore);

        fromFeature = type || fromFeature;

        let currentVersionName = "标准版";

        if (isPremium) {
            currentVersionName = "高级版";
        } else if (isGlory) {
            currentVersionName = "荣誉版";
        } else if (isGloryV1) {
            currentVersionName = "荣誉版V1";
        }

        switch (type) {
            case PremiumFunctionType.ORDER_LIST_ORDER_NUMBER_500:
            case PremiumFunctionType.ORDER_LIST_ORDER_NUMBER_1000: {
                if (!isPremium) {
                    // 限制高级版
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shopName}】店铺所属店铺为【{ownerShopName}】店铺，升级【{ownerShopName}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shopName}】店铺是${currentVersionName}，单次显示和打印500个订单以上属于高级版本功能，欢迎【${shopName}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }

                break;
            }
            case PremiumFunctionType.CROSS_SHOP_PRINT: {
                if (!isPremium) {
                    // 限制高级版
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shopName}】店铺所属店铺为【{ownerShopName}】店铺，升级【{ownerShopName}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shopName}】店铺是${currentVersionName}，跨店铺打单属于高级版本功能，欢迎【${shopName}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }

                break;
            }

            case PremiumFunctionType.SUB_ACCOUNT_NUMBER_5: {
                /** 不包含正在删除的账号 */
                const wechatUsers = globalStore.users.filter(
                    (user) => user.type === UserType.WECHAT && user.status !== UserStatus.DELETED,
                );
                const wechatCount = wechatUsers.length;
                const maxWechatCount = [
                    ShopType.TAOBAO_DROP_SHIPPING_SUPPLIER,
                    ShopType.PDD_DROP_SHIPPING_SUPPLIER,
                    ShopType.JD_DROP_SHIPPING_SUPPLIER,
                    ShopType.DOUDIAN_DROP_SHIPPING_SUPPLIER,
                    ShopType.OTHER_DROP_SHIPPING_SUPPLIER,
                ].includes(shop?.type)
                    ? 30
                    : 5;

                if (isPremium) {
                    if (wechatCount >= maxWechatCount) {
                        Modal.info({
                            title: `【${shop.name}】店铺是高级版本，最多只能添加${maxWechatCount}个微信子账号，您当前已有${maxWechatCount}个，不能继续添加`,
                            okText: "知道了",
                        });

                        return false;
                    }
                } else {
                    if (wechatCount >= 2) {
                        modalStore.openModal(NeedUpgradeModal, {
                            title: `【${shop.name}】店铺是${currentVersionName}，最多只能有2个微信子账号。当前最少已有2个，不能继续添加`,
                            okText: "升级版本-可多添加3个子账号",
                            shopId,
                            type,
                            fromFeature,
                            modalStore,
                        });

                        return false;
                    }
                }

                break;
            }

            case PremiumFunctionType.MANUAL_ORDER_SHOP_NUMBER_2: {
                const ownerManualShops = globalStore.shops.filter(
                    (it) => it.type === ShopType.MANUAL && it.ownerShopId === shopId,
                );

                // 当前店铺拥有的手工店铺数量
                const ownerManualShopCount = ownerManualShops.length;

                if (isPremium) {
                    if (ownerManualShopCount >= 2) {
                        Modal.info({
                            title: `【${shop.name}】店铺是高级版本，每个最多只能有2个手工店铺。当前最少已有2个手工店铺，不能继续添加`,
                        });

                        return false;
                    }
                } else {
                    if (ownerManualShopCount >= 2) {
                        Modal.error({
                            title: "发货易内每个店铺最多只能有2个手工店铺。当前最少已有2个手工店铺，不能继续添加",
                        });

                        return false;
                    }

                    if (ownerManualShopCount === 1) {
                        modalStore.openModal(NeedUpgradeModal, {
                            title: `【${shop.name}】店铺是${currentVersionName}，每个最多只能有1个手工店铺。当前最少已有1个，不能继续添加`,
                            okText: "升级版本-可多添加1个手工店铺",
                            shopId,
                            type,
                            fromFeature,
                            modalStore,
                        });

                        return false;
                    }
                }

                break;
            }

            case PremiumFunctionType.LOGISTICS_WARNING_ORDER_LIST:
            case PremiumFunctionType.ORDER_LOGISTICS_WARNING: {
                if (!isPremium) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shopName}】店铺所属店铺为【{ownerShopName}】店铺，升级【{ownerShopName}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，${
                            PremiumFunctionTypeNameMap[type as string]
                        }属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type: PremiumFunctionType.ORDER_LOGISTICS_WARNING,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }

                break;
            }

            case PremiumFunctionType.ORDER_SPLITTING: {
                if (isGlory) {
                    await showGloryTip(ownerShop ? ownerShop.name : shop.name, type);
                }

                if (!(isPremium || isGlory)) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，拆分订单和取消拆分属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }

                break;
            }

            case PremiumFunctionType.LOGISTICS_AUDIT: {
                if (isGlory || isGloryV1) {
                    await showGloryTip(ownerShop ? ownerShop.name : shop.name, type);
                }

                if (!(isPremium || isGlory || isGloryV1)) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，快递对账属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }
                break;
            }

            case PremiumFunctionType.OPERATION_LOG: {
                if (isGlory) {
                    await showGloryTip(ownerShop ? ownerShop.name : shop.name, type);
                }

                if (!(isPremium || isGlory)) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，查询操作日志属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }
                break;
            }

            case PremiumFunctionType.ADD_PDD_FDS_SHOP:
            case PremiumFunctionType.ADD_ALIBABA_SUPPLIER_SHOP: {
                if (!isPremium) {
                    const shopTypeText =
                        type === PremiumFunctionType.ADD_PDD_FDS_SHOP ? "拼多多官方代发" : "阿里巴巴供应商";

                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，添加${shopTypeText}店铺属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }

                break;
            }

            case PremiumFunctionType.PDD_FDS_SYNC_ORDER: {
                if (!isPremium) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，同步拼多多官方代发订单属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type: PremiumFunctionType.ADD_PDD_FDS_SHOP,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }
                break;
            }
            case PremiumFunctionType.PDD_FULFILLMENT_SYNC_ORDER: {
                if (!isPremium) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，同步拼多多跨境店铺订单属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type: PremiumFunctionType.ADD_PDD_FULFILLMENT_SHOP,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }
                break;
            }

            case PremiumFunctionType.ADD_PDD_FULFILLMENT_SHOP: {
                if (!isPremium) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，添加拼多多跨境店铺属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }

                break;
            }

            case PremiumFunctionType.TOP_SELLING_PRODUCT_PRINTING: {
                if (!isPremium) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，爆款打单属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }

                break;
            }

            case PremiumFunctionType.WAYBILL_STATS: {
                if (!isPremium) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，运单号统计属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }
                break;
            }
            // TODO: 暂时保存，后续等前端缓存消除了，再去除
            case PremiumFunctionType.ORDER_SEARCH_AFFILIATE_MARKETER: {
                if (!isPremium) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，搜索或导出带货达人信息属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }
                break;
            }

            case PremiumFunctionType.PRE_SHIPPING: {
                if (!isPremium) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，预发货属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }
                break;
            }
            case PremiumFunctionType.MULTI_PACKAGE_PRINTING: {
                if (!isPremium) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，自动分包设置属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }

                break;
            }

            case PremiumFunctionType.AUTO_RATE: {
                if (!isPremium) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，自动评价属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }

                break;
            }
            case PremiumFunctionType.FREQUENTLY_USED_ORDER_SEARCH_CRITERIA: {
                if (!isPremium) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，常用搜索属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }

                break;
            }

            case PremiumFunctionType.WMS: {
                if (!isPremium) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，库存管理属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }

                break;
            }

            case PremiumFunctionType.EXPORT_ALL_SEARCHED_ORDER_INFO: {
                if (!isPremium) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，导出搜索的全部订单，导出搜索的全部订单备货单和导出搜索的全部订单运单号属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }

                break;
            }

            case PremiumFunctionType.SHIPMENT_STATS: {
                if (!isPremium) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，发货统计属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }

                break;
            }

            case PremiumFunctionType.ORDER_VOLUME_LIMIT: {
                if (!isPremium) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    const orderVolumeTipName = getOrderVolumeTipName(shop.type);

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，
                        ，每月最多${orderVolumeTipName}
                        ${getOrderVolumeLimit(shop.type, appVersion)}
                        个订单，升级成高级版后可${orderVolumeTipName}
                        ${getOrderVolumeLimit(shop.type, AppVersion.PREMIUM)}
                        个订单。欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }

                break;
            }

            case PremiumFunctionType.LOGISTICS_AGING_PREDICT: {
                if (!isPremium) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，预估配送时效属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }

                break;
            }

            case PremiumFunctionType.BLACKLIST: {
                if (!isPremium) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，黑名单属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }

                break;
            }

            case PremiumFunctionType.ABNORMAL_CHECK_HOURS_AFTER_ORDER_SHIPPED: {
                if (!isPremium) {
                    const content = ownerShop ? (
                        <span className="text-error">
                            【{shop.name}】店铺所属店铺为【{ownerShop.name}】店铺，升级【{ownerShop.name}】店铺即可
                        </span>
                    ) : null;

                    modalStore.openModal(NeedUpgradeModal, {
                        title: `【${shop.name}】店铺是${currentVersionName}，设置发货后多少小时内检测异常订单属于高级版本功能，欢迎【${shop.name}】店铺升级后使用`,
                        content,
                        shopId,
                        type,
                        fromFeature,
                        modalStore,
                    });

                    return false;
                }

                break;
            }

            default:
                break;
        }

        return true;
    }
}
